import React, { useState, useEffect } from 'react';
import { useConfig } from '../config/ConfigContext';

// Simple mobile detection using window width
const isMobile = () => window.innerWidth <= 768;

/**
 * PDFViewer component for displaying PDF files embedded in the application
 * Provides responsive PDF viewing with controls and fallback options
 * @param {Object} props - Component props
 * @param {string} props.pdfUrl - URL of the PDF to display
 * @param {string} props.title - Title of the publication
 * @param {string} props.author - Author of the publication
 * @param {Function} props.onClose - Function to call when closing the viewer
 * @returns {JSX.Element} PDFViewer component
 */
const PDFViewer = ({ pdfUrl, title = 'Document', author, onClose }) => {
  const { theme } = useConfig();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMessage, setShowMessage] = useState('');

  // Clean and validate PDF URL
  const cleanPdfUrl = (url) => {
    if (!url) return '';
    
    let cleanedUrl = url.trim();
    
    // Add protocol if missing
    if (cleanedUrl && !cleanedUrl.startsWith('http://') && !cleanedUrl.startsWith('https://')) {
      cleanedUrl = 'https://' + cleanedUrl;
    }
    
    // Handle Google Drive URLs - convert to direct view format
    if (cleanedUrl.includes('drive.google.com') || cleanedUrl.includes('docs.google.com')) {
      // Extract file ID from various Google Drive URL formats
      const fileIdMatch = cleanedUrl.match(/\/d\/([a-zA-Z0-9-_]+)/);
      if (fileIdMatch && fileIdMatch[1]) {
        const fileId = fileIdMatch[1];
        console.log('[PDFViewer] Converting Google Drive URL to preview format');
        return `https://drive.google.com/file/d/${fileId}/preview`;
      }
      
      // If already in preview format, return as-is
      if (cleanedUrl.includes('/preview')) {
        console.log('[PDFViewer] Google Drive URL already in preview format');
        return cleanedUrl;
      }
    }
    
    // For non-Google URLs, wrap with Google Docs Viewer for better compatibility
    // This helps bypass CORS and X-Frame-Options restrictions
    console.log('[PDFViewer] Wrapping non-Google URL with Google Docs Viewer');
    return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(cleanedUrl)}`;
  };

  const processedPdfUrl = cleanPdfUrl(pdfUrl);

  // Check if fullscreen is supported
  const isFullscreenSupported = () => {
    return !!(
      document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled
    );
  };

  // Detect iOS devices
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };

  // Show user message
  const showUserMessage = (message, duration = 3000) => {
    setShowMessage(message);
    setTimeout(() => setShowMessage(''), duration);
  };

  // Fullscreen functionality
  const enterFullscreen = async () => {
    const viewerElement = document.getElementById('pdf-viewer-container');
    if (!viewerElement) return;
    
    // Check if fullscreen is supported
    if (!isFullscreenSupported()) {
      // iOS devices don't support fullscreen API
      if (isIOS()) {
        // Fallback: Open PDF in new tab with explanation
        showUserMessage('iOS doesn\'t support fullscreen. Opening in new tab...');
        setTimeout(() => {
          window.open(processedPdfUrl, '_blank');
        }, 1000);
        return;
      }
      
      // Other browsers without fullscreen support
      showUserMessage('Fullscreen not supported in this browser');
      return;
    }
    
    try {
      if (viewerElement.requestFullscreen) {
        await viewerElement.requestFullscreen();
      } else if (viewerElement.webkitRequestFullscreen) {
        await viewerElement.webkitRequestFullscreen();
      } else if (viewerElement.mozRequestFullScreen) {
        await viewerElement.mozRequestFullScreen();
      } else if (viewerElement.msRequestFullscreen) {
        await viewerElement.msRequestFullscreen();
      }
    } catch (err) {
      console.error('Failed to enter fullscreen:', err);
      showUserMessage('Unable to enter fullscreen mode');
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
    } catch (err) {
      console.error('Failed to exit fullscreen:', err);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      enterFullscreen();
    } else {
      exitFullscreen();
    }
  };

  useEffect(() => {
    if (processedPdfUrl) {
      setIsLoading(true);
      setHasError(false);
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  }, [processedPdfUrl]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement || 
                     !!document.webkitFullscreenElement || 
                     !!document.mozFullScreenElement ||
                     !!document.msFullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Handle iframe load
  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  // Handle iframe error
  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Open PDF in new tab - use original URL for direct access
  const openInNewTab = () => {
    if (pdfUrl) {
      // Use original URL so it opens directly without wrapper
      window.open(pdfUrl, '_blank');
      console.log('[PDFViewer] Opening original PDF URL in new tab:', pdfUrl);
    }
  };

  // Toggle controls visibility
  const toggleControls = () => {
    setShowControls(!showControls);
  };

  // Download PDF
  const downloadPdf = () => {
    if (pdfUrl) {
      // Use original URL for download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = title ? `${title}.pdf` : 'document.pdf';
      link.target = '_blank'; // Open in new tab if download attribute doesn't work
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log('[PDFViewer] Attempting to download PDF:', pdfUrl);
    }
  };


  const viewerStyles = {
    container: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      zIndex: 2147483648, // Higher than story modal (2147483647)
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      backgroundColor: theme.colors.primary,
      color: 'white',
      padding: '16px 20px',
      display: (isFullscreen || !showControls) ? 'none' : 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '10px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease'
    },
    titleSection: {
      flex: 1,
      minWidth: '200px'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      margin: 0,
      lineHeight: 1.2
    },
    author: {
      fontSize: '14px',
      opacity: 0.9,
      margin: '4px 0 0 0'
    },
    controls: {
      display: 'flex',
      gap: '10px',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    button: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'background-color 0.2s ease',
      whiteSpace: 'nowrap'
    },
    closeButton: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      border: 'none',
      padding: '8px 12px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold'
    },
    minimalToggleBar: {
      position: 'fixed',
      top: '16px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: theme.colors.primary,
      color: 'white',
      padding: '8px 16px',
      borderRadius: '20px',
      display: (!showControls && !isFullscreen) ? 'flex' : 'none',
      alignItems: 'center',
      gap: '8px',
      zIndex: 2147483649,
      cursor: 'pointer',
      fontSize: '14px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
      transition: 'all 0.3s ease'
    },
    content: {
      flex: 1,
      position: 'relative',
      backgroundColor: '#f0f0f0'
    },
    iframe: {
      width: '100%',
      height: '100%',
      border: 'none',
      backgroundColor: 'white'
    },
    loading: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      color: theme.colors.primary,
      fontSize: '18px',
      textAlign: 'center'
    },
    error: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      color: '#666',
      fontSize: '16px',
      textAlign: 'center',
      padding: '20px',
      maxWidth: '400px'
    },
    expandButtonOverlay: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      width: '40px',
      height: '40px',
      cursor: 'pointer',
      backgroundColor: 'transparent',
      border: 'none',
      zIndex: 10,
      // Debug styling - remove after testing
      // backgroundColor: 'rgba(255, 0, 0, 0.3)',
    },
    fullscreenExitButton: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: 'white',
      border: 'none',
      fontSize: '20px',
      fontWeight: 'bold',
      cursor: 'pointer',
      zIndex: 2147483650, // Highest z-index for fullscreen exit button
      display: isFullscreen ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      opacity: 0.7
    },
    messageOverlay: {
      position: 'fixed',
      top: '80px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '14px',
      zIndex: 2147483651, // Highest z-index for message overlay
      opacity: showMessage ? 1 : 0,
      visibility: showMessage ? 'visible' : 'hidden',
      transition: 'opacity 0.3s, visibility 0.3s'
    }
  };

  // Mobile responsive styles
  const mobileStyles = `
    @media (max-width: 768px) {
      .pdf-header {
        padding: 12px 16px !important;
      }
      
      .pdf-title {
        font-size: 16px !important;
      }
      
      .pdf-controls {
        gap: 8px !important;
      }
      
      .pdf-button {
        padding: 6px 12px !important;
        font-size: 13px !important;
      }
    }
    
    @media (max-width: 480px) {
      .pdf-header {
        flex-direction: column !important;
        align-items: flex-start !important;
        gap: 12px !important;
      }
      
      .pdf-controls {
        width: 100% !important;
        justify-content: space-between !important;
      }
    }
  `;

  if (!processedPdfUrl) {
    return (
      <div style={viewerStyles.container}>
        <div style={viewerStyles.error}>
          <h3>PDF Not Available</h3>
          <p>The PDF file for this publication is not available or the link is invalid.</p>
          <button 
            style={{
              ...viewerStyles.button,
              backgroundColor: theme.colors.primary,
              marginTop: '16px'
            }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: mobileStyles }} />
      <div id="pdf-viewer-container" style={viewerStyles.container}>
        {/* User message overlay */}
        {showMessage && (
          <div style={viewerStyles.messageOverlay}>
            {showMessage}
          </div>
        )}
        
        {/* Minimal toggle bar when header is hidden */}
        {!showControls && !isFullscreen && (
          <div 
            style={viewerStyles.minimalToggleBar}
            onClick={toggleControls}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.accent || theme.colors.primary;
              e.currentTarget.style.transform = 'translateX(-50%) scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.primary;
              e.currentTarget.style.transform = 'translateX(-50%) scale(1)';
            }}
          >
            <span>☰</span>
            <span>Show Controls</span>
          </div>
        )}
        
        {/* Minimalist fullscreen exit button */}
        <button
          style={viewerStyles.fullscreenExitButton}
          onClick={exitFullscreen}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.7';
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
          }}
          title="Exit fullscreen"
          aria-label="Exit fullscreen"
        >
          ✕
        </button>

        {/* Header with controls */}
        <header className="pdf-header" style={viewerStyles.header}>
          <div style={viewerStyles.titleSection}>
            <h1 className="pdf-title" style={viewerStyles.title}>{title}</h1>
            {author && <p style={viewerStyles.author}>by {author}</p>}
          </div>
          
          <div className="pdf-controls" style={viewerStyles.controls}>
            <button
              className="pdf-button"
              style={viewerStyles.button}
              onClick={toggleControls}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              title="Toggle header"
            >
              ☰
            </button>
            
            <button
              className="pdf-button"
              style={viewerStyles.button}
              onClick={openInNewTab}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              title="Open in new tab"
            >
              Open in New Tab
            </button>
            
            <button
              className="pdf-button"
              style={viewerStyles.button}
              onClick={downloadPdf}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              title="Download PDF"
            >
              Download
            </button>
            
            <button
              style={viewerStyles.closeButton}
              onClick={onClose}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              title="Close viewer"
            >
              ✕
            </button>
          </div>
        </header>

        {/* PDF Content */}
        <div style={viewerStyles.content}>
          {isLoading && (
            <div style={viewerStyles.loading}>
              <div>Loading PDF...</div>
              <div style={{ 
                marginTop: '16px', 
                fontSize: '14px', 
                opacity: 0.7 
              }}>
                This may take a few moments
              </div>
            </div>
          )}
          
          {hasError && (
            <div style={viewerStyles.error}>
              <h3>Unable to Display PDF</h3>
              <p>The PDF cannot be embedded due to security restrictions. You can still access it:</p>
              <div style={{ marginTop: '16px' }}>
                <button
                  style={{
                    ...viewerStyles.button,
                    backgroundColor: theme.colors.primary,
                    marginRight: '10px'
                  }}
                  onClick={openInNewTab}
                >
                  Open in New Tab
                </button>
                <button
                  style={{
                    ...viewerStyles.button,
                    backgroundColor: theme.colors.accent
                  }}
                  onClick={downloadPdf}
                >
                  Download PDF
                </button>
              </div>
              <p style={{ marginTop: '12px', fontSize: '12px', opacity: 0.7 }}>
                Some PDFs cannot be embedded due to their host's security settings.
              </p>
            </div>
          )}
          
          {!hasError && (
            <>
              <iframe
                src={processedPdfUrl}
                style={viewerStyles.iframe}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                title={`PDF: ${title}`}
                sandbox={isIOS() ? "allow-same-origin allow-scripts allow-forms allow-popups" : "allow-same-origin allow-scripts allow-forms"}
                allowFullScreen={true}
                webkitallowfullscreen="true"
                mozallowfullscreen="true"
              />
              {/* Invisible overlay button to intercept Google Drive expand button clicks */}
              <button
                style={viewerStyles.expandButtonOverlay}
                onClick={toggleFullscreen}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  toggleFullscreen();
                }}
                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PDFViewer;